import { UseQueryResult } from '@tanstack/react-query';
import { UseInfiniteQueryResult } from '@tanstack/react-query/src/types';
import React from 'react';

import { Merchant } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';

import { ChartData, Legends } from './components/charts/types';

export enum RowType {
  Metric = 'metric',
  Menu = 'menu',
  Item = 'item',
  Content = 'content',
  Raw = 'raw',
}

type Segment = string extends `${string}.${string}` ? never : string;
type SegmentedPath = `${Segment}.${Segment}`;
export type JsonPath = SegmentedPath | `${SegmentedPath}.${Segment}`;

export type GrowthTrend = 'positive' | 'negative' | 'neutral';

export type MetricRow = {
  type: RowType.Metric;
  title: JsonPath;
  titleTooltip?: JsonPath;
  subtitle?: React.ReactNode;
  metrics: string | number;
  growthRate?: string;
  growthTrend?: GrowthTrend;
};

export type MenuRow = {
  type: RowType.Menu;
  icon: React.ReactNode;
  title: string;
  itemsCount: number;
};

export type ContentRow = {
  type: RowType.Content | RowType.Raw;
  content: React.ReactNode;
  key: string;
};

export type SummaryTableRows = MetricRow[] | MenuRow[] | ContentRow[];

export enum OrderType {
  Regular = 'REGULAR',
  WoltPlus = 'WOLT+',
}

export type PerformanceBarChartData = ChartData<Legends<OrderType>>;

export enum ChartCategory {
  TOTAL_SALES = 'totalSales',
  TOTAL_ORDERS = 'totalOrders',
  AVG_BASKET_SIZE = 'avgBasketSize',
  AVG_BASKET_SIZE_ITEMS = 'avgBasketSizeItems',
}

export type BarChartDataByCategory = Record<ChartCategory, PerformanceBarChartData>;

export enum Period {
  TODAY = 'todayDateRange',
  YESTERDAY = 'yesterdayDateRange',
  LAST_WEEK = 'lastWeekDateRange',
  THIS_MONTH = 'thisMonthDateRange',
  LAST_28_DAYS = 'last28daysDateRange',
  SEVEN_DAYS_AGO = 'sevenDaysAgoDateRange',
}

export type DateRange = {
  from: Date | undefined;
  to?: Date | undefined;
  name?: Period;
};

type GenericEntity = {
  id: string;
  isWoltPlus: boolean;
  venues: Venue[];
};

export type VenueEntity = GenericEntity & {
  type: 'venue';
  venue: Venue;
};

export type MerchantEntity = GenericEntity & {
  type: 'merchant';
  merchant: Merchant;
};

export type BrandEntity = GenericEntity & {
  type: 'brand';
};

export type EnterpriseEntity = GenericEntity & {
  type: 'enterprise';
};

export type InsightEntity = VenueEntity | MerchantEntity | BrandEntity | EnterpriseEntity;

type InsightResponseCommon = {
  refetch: () => void;
  isFetching: boolean;
};

type SuccessMixin = {
  isError: false;
  isLoading: false;
};

type LoadingMixin = {
  isError: false;
  isLoading: true;
};

type ErrorMixin = {
  isError: true;
  isLoading: false;
};

type InsightError = ErrorMixin & {
  selected: { data: undefined };
  previous: { data: undefined };
};

type InsightSuccess<T> = SuccessMixin & {
  selected: InsightResponse<T> & SuccessMixin;
  previous: (InsightResponse<T> & SuccessMixin) | { data: undefined };
};

type InsightLoading = LoadingMixin & {
  selected: { data: undefined };
  previous: { data: undefined };
};

export type InsightResponseWithPreviousInterval<T> = InsightResponseCommon &
  (InsightSuccess<T> | InsightError | InsightLoading);

export type InsightResponse<T> = UseQueryResult<T>;

export type InsightResponsePaginated<T> = UseInfiniteQueryResult<T>;
